import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import axios from 'axios'
import {EmptyObject} from 'chart.js/types/basic'
import {isEmpty} from 'lodash'
import {KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {SortType} from 'rsuite-table/lib/@types/common'
import IconButton from 'rsuite/IconButton'
import Pagination from 'rsuite/Pagination'
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import {UserModel} from 'src/app/modules/auth/models/UserModel'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import * as user from 'src/app/modules/userMgmt'
import {API_URL} from 'src/cms/constants/EndPoint'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import {columnModel} from 'src/cms/helpers/components/rsuiteTable/Models/columnModel'
import {IUserState} from '..'
import styled from 'styled-components'
import AddUser from './Adduser'

const Cell = Table.Cell

type TabKey = 'users' | 'staff'

const USERS_TAB: TabKey = 'users'
const STAFF_TAB: TabKey = 'staff'

// Same flag as `IS_INSTANT_APP` in src/app/App.tsx, read straight off the runtime config
// rather than imported: RoutesList.ts imports this file eagerly, so App.tsx's own module
// body has not run yet at this point and importing the const would hit its TDZ.
const IS_INSTANT_APP = window.__RUNTIME_CONFIG__?.REACT_APP_NAME === 'instant'

/**
 * The second tab is the same table in both builds, but the instant build talks to a
 * different backend: `gibl-online-account-opening-api` has no `outsourced-staff` route,
 * it exposes RO users instead. `/ro-user/list` returns the WHOLE list in one response
 * (no page/limit/search support), so that variant paginates and searches client-side --
 * `serverPaginated` is what switches between the two strategies.
 */
const STAFF_TAB_CONFIG = IS_INSTANT_APP
  ? {
      label: 'RO Users',
      moduleName: 'RO Users',
      dataCy: 'datatable-ro-users',
      listUrl: `${API_URL}/ro-user/list`,
      syncUrl: `${API_URL}/sync/ro-employee`,
      serverPaginated: false,
      loadErrorMessage: 'Failed to load RO users',
      syncSuccessMessage: 'RO user sync job started successfully',
      syncErrorMessage: 'RO user sync failed',
    }
  : {
      label: 'Outsourced Staff',
      moduleName: 'Outsourced Staff',
      dataCy: 'datatable-outsourced-staff',
      listUrl: `${API_URL}/outsourced-staff`,
      syncUrl: `${API_URL}/sync/outsourced-staff`,
      serverPaginated: true,
      loadErrorMessage: 'Failed to load outsourced staff',
      syncSuccessMessage: 'Employee sync job started successfully',
      syncErrorMessage: 'Employee synced failed',
    }

const USERS_SYNC_URL = `${API_URL}/sync/employee`
const USERS_SYNC_SUCCESS = 'Employee sync job started successfully'
const USERS_SYNC_ERROR = 'Employee synced failed'

const INITIAL_PARAMS: StateParamsModel = {page: 1, limit: 10}
const PAGINATION_LIMITS = [10, 20, 50, 100]
const EMPTY_ROWS: any[] = []

const StatusCell = (props: any) => (
  <Cell {...props}>
    {(rowData: any) =>
      rowData?.status ? (
        <Tag color='green' size='sm'>
          Active
        </Tag>
      ) : (
        <Tag color='red' size='sm'>
          Inactive
        </Tag>
      )
    }
  </Cell>
)

type ActionCellProps = {
  rowData?: any
  onEdit: (rowData: any) => void
  onDelete: (rowData: any) => void
  onToggle: (rowData: any) => void
  [key: string]: any
}

// Module scope on purpose: an inline component is a brand new element type on every
// render, which makes rsuite tear down and remount every action cell in the table.
const ActionCell = ({rowData, dataKey, onEdit, onDelete, onToggle, ...props}: ActionCellProps) => (
  <Cell {...props} className='link-group' data-cy='link-user-group'>
    <CheckPermissions type='Edit'>
      <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit </Tooltip>}>
        <IconButton
          appearance='subtle'
          onClick={() => onEdit(rowData)}
          data-cy='edit-button'
          icon={<Edit2 />}
        />
      </Whisper>
    </CheckPermissions>
    <CheckPermissions type='Delete'>
      <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
        <IconButton
          appearance='subtle'
          onClick={() => onDelete(rowData)}
          data-cy='delete-button'
          icon={<TrashIcon />}
        />
      </Whisper>
    </CheckPermissions>
    <CheckPermissions type='Edit'>
      <Whisper placement='top' trigger='hover' speaker={<Tooltip>Status</Tooltip>}>
        <Toggle
          size='sm'
          checked={rowData?.status}
          onClick={() => onToggle(rowData)}
          data-cy='toggle-button'
        />
      </Whisper>
    </CheckPermissions>
  </Cell>
)

// Both column sets are static -- they close over nothing, so they never need rebuilding.
//
// Responsiveness comes from RSuiteTable (src/cms/helpers/components/rsuiteTable/index.tsx):
// it only lets a column flex when that column declares NO explicit `width`
// (`flexGrow = column.flexGrow ?? (hasExplicitWidth || column.fixed ? undefined : 1)`).
// So the text columns below deliberately omit `width` and set a `minWidth` floor instead,
// exactly like Name/Branch/Email in the Users table; only the two narrow fixed-content
// columns (S.N., Status) keep a hard width. RSuiteTable also wraps a plain `<Cell dataKey=... />` in a
// tooltip cell, so a value clipped at a narrow width is still readable on hover.

// Shape returned by `/ro-user/list` (RoUserController::get_list):
// {id: username, name, username, email, country, status: bool}
const RO_USER_COLUMNS: columnModel[] = [
  {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
  {
    label: 'Name',
    dataKey: 'name',
    flexGrow: 1,
    minWidth: 200,
    cell: <Cell dataKey='name' />,
    sortable: false,
  },
  {
    label: 'Username',
    dataKey: 'username',
    flexGrow: 1,
    minWidth: 150,
    cell: <Cell dataKey='username' />,
    sortable: false,
  },
  {
    label: 'Email',
    dataKey: 'email',
    flexGrow: 1,
    minWidth: 220,
    cell: <Cell dataKey='email' />,
    sortable: false,
  },
  {
    label: 'Country',
    dataKey: 'country',
    flexGrow: 1,
    minWidth: 130,
    cell: <Cell dataKey='country' />,
    sortable: false,
  },
  {label: 'Status', dataKey: 'status', width: 85, cell: <StatusCell />, sortable: false},
]

const OUTSOURCED_STAFF_COLUMNS: columnModel[] = [
  {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
  {
    label: 'Name',
    dataKey: 'name',
    flexGrow: 1,
    minWidth: 200,
    cell: <Cell dataKey='name' />,
    sortable: false,
  },
  {
    label: 'Username',
    dataKey: 'username',
    flexGrow: 1,
    minWidth: 150,
    cell: <Cell dataKey='username' />,
    sortable: false,
  },
  {
    label: 'Staff Position',
    dataKey: 'position',
    flexGrow: 1,
    minWidth: 180,
    cell: <Cell dataKey='position' />,
    sortable: false,
  },
  {
    label: 'Inside/Outside',
    dataKey: 'inside_outside',
    flexGrow: 1,
    minWidth: 140,
    cell: <Cell dataKey='inside_outside' />,
    sortable: false,
  },
  {
    label: 'Province',
    dataKey: 'province_branch',
    flexGrow: 1,
    minWidth: 140,
    cell: <Cell dataKey='province_branch' />,
    sortable: false,
  },
  {
    label: 'Branch Department',
    dataKey: 'branch_department',
    flexGrow: 1,
    minWidth: 180,
    cell: <Cell dataKey='branch_department' />,
    sortable: false,
  },
  {label: 'Status', dataKey: 'status', width: 85, cell: <StatusCell />, sortable: false},
]

const STAFF_COLUMNS = IS_INSTANT_APP ? RO_USER_COLUMNS : OUTSOURCED_STAFF_COLUMNS

// Params reach this page from DesignComponent, which spreads its own keys into whatever
// object it was handed, so the same query can arrive with a different key set or order
// (`{page,limit}` vs `{page,limit,search:'',search_type:''}`). Comparing raw JSON treats
// those as different queries and refetches; comparing sorted, non-empty entries doesn't.
const paramsKey = (source: StateParamsModel) =>
  JSON.stringify(
    Object.entries(source)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b))
  )

const TABS: {key: TabKey; label: string}[] = [
  {key: USERS_TAB, label: 'Users'},
  {key: STAFF_TAB, label: STAFF_TAB_CONFIG.label},
]

/**
 * A segmented control rather than folder tabs: there are exactly two populations here and
 * they carry equal weight, so this reads as switching the view rather than descending into
 * a sub-section. Built from buttons instead of rsuite's Nav so the tablist keyboard
 * contract (arrow keys, roving tabindex) is real rather than approximated, and so the
 * styling doesn't have to fight `.rs-nav-tabs`.
 *
 * No brand colour: red is already carrying the sidebar and header, and the active segment
 * reads clearly from the white surface alone.
 */
const TabList = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  margin-bottom: 18px;
  border-radius: 10px;
  background: #f2f4f7;
  box-shadow: inset 0 0 0 1px rgba(16, 24, 40, 0.04);

  [role='tab'] {
    appearance: none;
    border: 0;
    background: transparent;
    border-radius: 7px;
    padding: 8px 20px;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.4;
    white-space: nowrap;
    color: #667085;
    cursor: pointer;
    transition: color 120ms ease, background-color 120ms ease, box-shadow 120ms ease;
  }

  [role='tab']:hover[aria-selected='false'] {
    color: #175cd3;
    background: rgba(23, 92, 211, 0.06);
  }

  /* #175CD3 rather than a lighter blue: white 13.5px text on it clears 4.5:1, which the
     600-weight blues do not. */
  [role='tab'][aria-selected='true'] {
    font-weight: 600;
    color: #ffffff;
    background: #175cd3;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.16);
  }

  [role='tab']:focus-visible {
    outline: 2px solid #175cd3;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    display: flex;
    width: 100%;

    [role='tab'] {
      flex: 1 1 0;
      padding: 8px 10px;
      text-align: center;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    [role='tab'] {
      transition: none;
    }
  }
`

const matchesSearch = (row: any, needle: string) =>
  Object.values(row ?? {}).some(
    (value) =>
      (typeof value === 'string' || typeof value === 'number') &&
      String(value).toLowerCase().includes(needle)
  )

const UserManagementIndex = () => {
  const dispatch = useDispatch()
  const userList: IUserState = useSelector((state: any) => state.userManagement)
  const {
    activateSuccess,
    deactivateSuccess,
    singleActivateSuccess,
    singleDeactivateSuccess,
    deleteSuccess,
  } = userList

  const [activeTab, setActiveTab] = useState<TabKey>(USERS_TAB)

  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState('Add')
  const [editSelectedData, setEditCheckedData] = useState<UserModel | EmptyObject>({})
  const [selectedData, setSelectedData] = useState<Array<string>>([])
  // alert modal
  const [alertOpen, setAlertOpen] = useState(false)
  const [syncAlert, setSyncAlert] = useState(false)

  const [params, setParams] = useState<StateParamsModel>(INITIAL_PARAMS)
  const [staffParams, setStaffParams] = useState<StateParamsModel>(INITIAL_PARAMS)
  const [staffState, setStaffState] = useState<{rows: any[]; total: number; loading: boolean}>({
    rows: EMPTY_ROWS,
    total: 0,
    loading: false,
  })

  // Signature of the request each tab has already issued. Switching tabs alone never
  // changes it, so returning to a tab reuses what is already loaded instead of hitting
  // the API again. `undefined` means invalidated -- the next render for that tab refetches.
  const usersFetchKeyRef = useRef<string | undefined>(undefined)
  const staffFetchKeyRef = useRef<string | undefined>(undefined)
  // Guards against a slow response from an earlier page/tab landing after a newer one.
  const staffRequestIdRef = useRef(0)

  // The post-action effect below must fire on the success flags alone, but still needs the
  // current tab and params. Reading them through refs keeps them out of its dependency
  // list honestly -- this repo has no react-hooks lint plugin, so an exhaustive-deps
  // disable comment would itself break the build (see CLAUDE.md).
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab
  const paramsRef = useRef(params)
  paramsRef.current = params

  const tabListRef = useRef<HTMLDivElement>(null)

  const handleAlertOpen = useCallback(() => setAlertOpen(true), [])
  const handleAlertClose = useCallback(() => setAlertOpen(false), [])
  const handleSyncAlertOpen = useCallback(() => setSyncAlert(true), [])
  const handleSyncAlertClose = useCallback(() => setSyncAlert(false), [])
  const handleClose = useCallback(() => setOpen(false), [])

  const handleCheck = useCallback((data: Array<string>) => setSelectedData(data), [])

  // Bails out when the selection is already empty, so the repeated post-action clears
  // don't re-render the table for nothing.
  const clearSelection = useCallback(
    () => setSelectedData((prev) => (prev.length === 0 ? prev : [])),
    []
  )

  const fetchStaff = useCallback(async (queryParams: StateParamsModel) => {
    const requestId = ++staffRequestIdRef.current
    setStaffState((prev) => (prev.loading ? prev : {...prev, loading: true}))

    try {
      const res = await axios.get(STAFF_TAB_CONFIG.listUrl, {
        params: STAFF_TAB_CONFIG.serverPaginated ? queryParams : undefined,
      })
      if (requestId !== staffRequestIdRef.current) return

      const payload = res?.data?.data ?? res?.data
      const rows: any[] = Array.isArray(payload) ? payload : payload?.user ?? []
      const total = STAFF_TAB_CONFIG.serverPaginated
        ? res?.data?.data?.meta?.total ?? res?.data?.data?.total ?? rows.length
        : rows.length

      setStaffState({rows, total, loading: false})
    } catch {
      if (requestId !== staffRequestIdRef.current) return
      setStaffState((prev) => ({...prev, loading: false}))
      toast.error(STAFF_TAB_CONFIG.loadErrorMessage)
    }
  }, [])

  useEffect(() => {
    if (activeTab !== USERS_TAB) return

    const fetchKey = paramsKey(params)
    if (usersFetchKeyRef.current === fetchKey) return

    usersFetchKeyRef.current = fetchKey
    dispatch(user.actions.getUsers(params))
  }, [dispatch, params, activeTab])

  useEffect(() => {
    if (activeTab !== STAFF_TAB) return

    // The RO endpoint ignores page/limit/search, so a single fetch covers every page.
    const fetchKey = STAFF_TAB_CONFIG.serverPaginated ? paramsKey(staffParams) : 'all'
    if (staffFetchKeyRef.current === fetchKey) return

    staffFetchKeyRef.current = fetchKey
    fetchStaff(staffParams)
  }, [activeTab, staffParams, fetchStaff])

  useEffect(() => {
    if (!singleActivateSuccess && !singleDeactivateSuccess) return

    toast.success(
      singleActivateSuccess ? 'User activated successfully' : 'User deactivated successfully'
    )
    clearSelection()
  }, [singleActivateSuccess, singleDeactivateSuccess, clearSelection])

  useEffect(() => {
    // This used to run on mount as well and fired a second getUsers alongside the params
    // effect -- two identical requests on every visit to the page.
    if (!activateSuccess && !deactivateSuccess && !deleteSuccess) return

    if (activateSuccess) toast.success('User activated successfully')
    if (deactivateSuccess) toast.success('User deactivated successfully')
    if (deleteSuccess) toast.success('User deleted successfully')

    clearSelection()
    if (activeTabRef.current === USERS_TAB) {
      usersFetchKeyRef.current = paramsKey(paramsRef.current)
      dispatch(user.actions.getUsers(paramsRef.current))
    } else {
      // Refetch when the Users tab is opened again rather than in the background.
      usersFetchKeyRef.current = undefined
    }
  }, [activateSuccess, deactivateSuccess, deleteSuccess, clearSelection, dispatch])

  // A bulk action with nothing selected used to call setAlertOpen during the render pass.
  useEffect(() => {
    if (!alertOpen || !isEmpty(selectedData)) return
    toast.error('No data selected')
    setAlertOpen(false)
  }, [alertOpen, selectedData])

  const handleEditRow = useCallback((rowData: any) => {
    setEditCheckedData(rowData)
    setActionType('Edit')
    setOpen(true)
  }, [])

  const handleDeleteRow = useCallback((rowData: any) => {
    setSelectedData([rowData?.id])
    setAlertOpen(true)
  }, [])

  const handleToggleAction = useCallback(
    (data: {[key: string]: any}) => {
      dispatch(
        data?.status
          ? user.actions.singleDeactivateUser(data.id)
          : user.actions.singleActivateUser(data.id)
      )
    },
    [dispatch]
  )

  const columns: columnModel[] = useMemo(
    () => [
      {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
      {label: 'Name', dataKey: 'name', flexGrow: 1, cell: <Cell dataKey='name' />, sortable: true},
      {
        label: 'Username',
        dataKey: 'userName',
        width: 120,
        cell: <Cell dataKey='userName' />,
        sortable: true,
      },
      {
        label: 'Branch',
        dataKey: 'branch',
        flexGrow: 1,
        cell: <Cell>{(rowData: any) => rowData?.branch?.title ?? '-'}</Cell>,
        sortable: true,
      },
      {
        label: 'Email',
        dataKey: 'email',
        flexGrow: 1,
        cell: <Cell dataKey='email' />,
        sortable: true,
      },
      {
        label: 'Mobile Number',
        dataKey: 'mobile_number',
        width: 140,
        cell: <Cell dataKey='mobile_number' />,
        sortable: true,
      },
      {
        label: 'Department',
        dataKey: 'department',
        width: 200,
        cell: <Cell>{(rowData: any) => rowData?.department?.name ?? '-'}</Cell>,
        sortable: true,
      },
      {
        label: 'Status',
        dataKey: 'status',
        width: 85,
        fixed: true,
        cell: <StatusCell />,
        sortable: false,
      },
      {
        label: 'Action',
        width: 120,
        fixed: true,
        align: 'center',
        cell: (
          <ActionCell
            dataKey='id'
            onEdit={handleEditRow}
            onDelete={handleDeleteRow}
            onToggle={handleToggleAction}
          />
        ),
      },
    ],
    [handleEditRow, handleDeleteRow, handleToggleAction]
  )

  const data = useMemo(() => {
    const users = userList?.data?.user
    if (!users?.length) return EMPTY_ROWS

    const offset = (params?.page - 1) * params?.limit
    return users.map((item: any, i: number) => ({
      sn: offset + i + 1,
      id: item?.id,
      name: item?.name,
      userName: item?.userName,
      email: item?.email,
      mobile_number: item?.mobile_number,
      status: item?.status,
      departmentId: item?.department?.id,
      grievance_manager: item?.grievanceManager,
      is_admin: item?.is_admin,
      branch_id: item?.branch_id,
      vendor: item?.vendor,
      branches: item?.branches,
      provinces: item?.provinces,
      branch: item?.branch,
      department: item?.department,
    }))
  }, [userList?.data?.user, params?.page, params?.limit])

  // Client-side search -- only reachable on the non-paginated RO endpoint.
  const staffMatchedRows = useMemo(() => {
    if (STAFF_TAB_CONFIG.serverPaginated) return staffState.rows

    const needle = String(staffParams.search ?? '')
      .trim()
      .toLowerCase()
    if (!needle) return staffState.rows

    return staffState.rows.filter((row) => matchesSearch(row, needle))
  }, [staffState.rows, staffParams.search])

  const staffTotal = STAFF_TAB_CONFIG.serverPaginated ? staffState.total : staffMatchedRows.length

  const staffData = useMemo(() => {
    const page = staffParams.page || 1
    const limit = staffParams.limit || 10
    const offset = (page - 1) * limit
    const pageRows = STAFF_TAB_CONFIG.serverPaginated
      ? staffMatchedRows
      : staffMatchedRows.slice(offset, offset + limit)

    return pageRows.map((item: any, i: number) => ({sn: offset + i + 1, ...item}))
  }, [staffMatchedRows, staffParams.page, staffParams.limit])

  const activateSelectedData = useCallback(() => {
    if (isEmpty(selectedData)) {
      toast.error('No data Selected')
      return
    }
    dispatch(user.actions.activateUsers(selectedData.map((id: string) => ({id}))))
  }, [dispatch, selectedData])

  const deactivateSelectedData = useCallback(() => {
    if (isEmpty(selectedData)) {
      toast.error('No data Selected')
      return
    }
    dispatch(user.actions.deactivateUsers(selectedData.map((id: string) => ({id}))))
  }, [dispatch, selectedData])

  const handleSort = useCallback((dataKey: string, sortType?: SortType) => {
    setParams((prev) => ({...prev, orderBy: dataKey, order: sortType}))
  }, [])

  const handleChangeLimit = useCallback((dataKey: number) => {
    setParams((prev) => ({...prev, limit: dataKey}))
  }, [])

  const handleAddModal = useCallback(() => {
    setEditCheckedData({})
    setActionType('Add')
    setOpen(true)
  }, [])

  const handleDelete = useCallback(() => {
    dispatch(user.actions.deleteUsers(selectedData.map((id) => ({id}))))
    setAlertOpen(false)
  }, [dispatch, selectedData])

  const handleRefresh = useCallback(() => {
    if (activeTab === STAFF_TAB) {
      staffFetchKeyRef.current = STAFF_TAB_CONFIG.serverPaginated
        ? paramsKey(staffParams)
        : 'all'
      fetchStaff(staffParams)
      return
    }
    usersFetchKeyRef.current = paramsKey(params)
    dispatch(user.actions.getUsers(params))
  }, [activeTab, dispatch, fetchStaff, params, staffParams])

  const handleTabSelect = useCallback(
    (key: TabKey) => {
      setActiveTab(key)
      clearSelection()
    },
    [clearSelection]
  )

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
      if (!step) return

      event.preventDefault()
      const next = (index + step + TABS.length) % TABS.length
      handleTabSelect(TABS[next].key)
      // The roving tabindex moves with the selection, so follow it with focus.
      tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
    },
    [handleTabSelect]
  )

  const handleSync = useCallback(async () => {
    setSyncAlert(false)

    const isStaffTab = activeTab === STAFF_TAB
    try {
      await axios.get(isStaffTab ? STAFF_TAB_CONFIG.syncUrl : USERS_SYNC_URL)
      toast.success(isStaffTab ? STAFF_TAB_CONFIG.syncSuccessMessage : USERS_SYNC_SUCCESS)
    } catch {
      toast.error(isStaffTab ? STAFF_TAB_CONFIG.syncErrorMessage : USERS_SYNC_ERROR)
    }
  }, [activeTab])

  return (
    <div className='shadow p-3 bg-white rounded' data-cy='shadow-user'>
      <TabList role='tablist' aria-label='User directory' ref={tabListRef}>
        {TABS.map((tab, index) => {
          const isActive = tab.key === activeTab
          return (
            <button
              key={tab.key}
              type='button'
              role='tab'
              id={`${tab.key}-tab`}
              aria-selected={isActive}
              aria-controls={`${tab.key}-panel`}
              // Roving tabindex: one stop for the whole group, arrows move within it.
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTabSelect(tab.key)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              data-cy={`${tab.key}-tab`}
            >
              {tab.label}
            </button>
          )
        })}
      </TabList>

      {activeTab === USERS_TAB && (
        <div role='tabpanel' id={`${USERS_TAB}-panel`} aria-labelledby={`${USERS_TAB}-tab`}>
          <DesignComponent
            moduleName='User Management'
            params={params}
            setParams={setParams}
            handleRefresh={handleRefresh}
            handleAddModal={handleAddModal}
            handleAlertOpen={handleAlertOpen}
            handleSyncAlertOpen={handleSyncAlertOpen}
            exportShow={false}
            importShow={false}
            enableMultiple={activateSelectedData}
            disableMultiple={deactivateSelectedData}
            syncButtonShow={true}
            syncButtonName='Sync'
          />
          <div className='datatable' data-cy='datatable-user'>
            <RSuiteTable
              showCheckbox={false}
              onChecked={handleCheck}
              columns={columns}
              data={data}
              checkedValues={selectedData}
              showLoading={userList?.loading}
              handleSort={handleSort}
            />
            <Pagination
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              className='mt-5'
              size='sm'
              layout={['total', '-', 'limit', '|', 'pager', 'skip']}
              total={userList?.data?.meta?.total || 0}
              limitOptions={PAGINATION_LIMITS}
              limit={params?.limit}
              activePage={params?.page}
              onChangePage={(value) => setParams((prev) => ({...prev, page: value}))}
              onChangeLimit={handleChangeLimit}
            />
            {open && (
              <AddUser
                open={open}
                params={params}
                handleClose={handleClose}
                actionType={actionType}
                editSelectedData={editSelectedData}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === STAFF_TAB && (
        <div role='tabpanel' id={`${STAFF_TAB}-panel`} aria-labelledby={`${STAFF_TAB}-tab`}>
          <DesignComponent
            moduleName={STAFF_TAB_CONFIG.moduleName}
            params={staffParams}
            setParams={setStaffParams}
            handleRefresh={handleRefresh}
            exportShow={false}
            importShow={false}
            addShow={false}
            deleteShow={false}
            handleAlertOpen={handleAlertOpen}
            handleSyncAlertOpen={handleSyncAlertOpen}
            toggleMultipleShow={false}
            syncButtonShow={true}
            syncButtonName='Sync'
          />
          <div className='datatable' data-cy={STAFF_TAB_CONFIG.dataCy}>
            <RSuiteTable
              showCheckbox={false}
              columns={STAFF_COLUMNS}
              data={staffData}
              showLoading={staffState.loading}
            />
            <Pagination
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              className='mt-5'
              size='sm'
              layout={['total', '-', 'limit', '|', 'pager', 'skip']}
              total={staffTotal}
              limitOptions={PAGINATION_LIMITS}
              limit={staffParams.limit}
              activePage={staffParams.page}
              onChangePage={(value) => setStaffParams((prev) => ({...prev, page: value}))}
              onChangeLimit={(value) =>
                setStaffParams((prev) => ({...prev, limit: value, page: 1}))
              }
            />
          </div>
        </div>
      )}

      {alertOpen && !isEmpty(selectedData) && (
        <DeleteModal handleClick={handleDelete} isOpen={alertOpen} handleClose={handleAlertClose} />
      )}
      {syncAlert && (
        <DeleteModal
          handleClick={handleSync}
          isOpen={syncAlert}
          handleClose={handleSyncAlertClose}
          title='sync'
        />
      )}
    </div>
  )
}

export default UserManagementIndex
